import { useMemo } from 'react';
import { UserCircle2 } from 'lucide-react';
import { getActiveDrillItemPath, getActiveNavItemLabel } from '../NavBar/nav-utils';
import type { NavItem } from '../NavBar/nav-types';
import {
  IdentityHeader,
  IdentityTile,
  TakeoverAside,
  takeoverShellClassName,
} from './TakeoverChrome';
import { TakeoverNavGroups, type TakeoverNavGroup } from './TakeoverNav';

export interface AccountShellViewProps {
  /** Account owner named in the identity header (the user's email or login). */
  subtitle: string;
  /** Current pathname, used to highlight the active account entry. */
  activePath: string;
  /** The account menu options (Profile, Settings, plugin user-menu routes). */
  items: NavItem[];
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * Presentational layout of the Account takeover: the shared aside chrome with
 * a "Back" chip + `esc` hint on top, an identity header naming the surface
 * ("Account" / the user's email), and exactly the account menu options as nav
 * — nothing else. Page content renders beside the aside. Mirrors the
 * Configure / Labs / Automations shells so entering and leaving the account
 * surface looks and behaves identically to every other takeover.
 *
 * The connected {@link AccountShell} wires nav sources, routing, and the
 * escape/back behavior; stories drive this view directly.
 */
export function AccountShellView({
  subtitle,
  activePath,
  items,
  onBack,
  children,
}: AccountShellViewProps) {
  // One group, no label: the account surface lists only the account menu
  // options, the same set the account dropdown exposes.
  const groups = useMemo<TakeoverNavGroup[]>(() => [{ label: '', items }], [items]);
  const activeItemPath = useMemo(
    () => getActiveDrillItemPath(items, activePath),
    [items, activePath],
  );
  const pageTitle = getActiveNavItemLabel(items, activePath, 'Account');

  return (
    <div className={takeoverShellClassName} data-testid="account-shell">
      <TakeoverAside
        back={{ label: 'Back', onClick: onBack, testId: 'account-back' }}
        header={
          <IdentityHeader
            tile={<IdentityTile icon={UserCircle2} />}
            title="Account"
            subtitle={subtitle}
          />
        }
        pageTitle={pageTitle}
        page={<div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
      >
        <nav
          aria-label="Account navigation"
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3 scrollbar-fade"
        >
          <TakeoverNavGroups
            groups={groups}
            activeItemPath={activeItemPath}
            testIdPrefix="account-nav"
          />
        </nav>
      </TakeoverAside>
    </div>
  );
}
